// People — the People/Groups/Pending/Guest/Archived tabs + members table with
// server-side pagination, debounced search and toolbar filters
// (POST /company-users/users; filter selections go as id arrays —
// roles/groups/tags/structureNodes/userStatus). Guests come from their own
// POST /guests/all ({page, limit, search?} only — no status/filters, so the
// Filter dropdown hides on that tab) and show just Person + Phone columns.
// Deleting a person is archive-first by product decision: the People tab has no
// Delete, only Archive. Pending lists two statuses with separate actions, all on
// POST company-users/update-invite-status: a `pending` request gets Accept /
// Reject (status ACCEPTED / REJECTED, gated by the invite extra) and an
// `admin_invite` gets Delete (status DELETED — withdraws it). Archived Delete
// removes the person (POST company-users/bulk-delete) — one call each for a row
// or a whole selection. Archive (People) and Restore (Archived) are the two
// lifecycle moves — both ride the bulk endpoint (actionType archive/restore)
// and sit in the selection bar as their own buttons, plus on the row kebab.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ColumnsMenu,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  TableToolbar,
  Tabs,
  type DataTableColumn,
  type FilterDropdownCategory,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useModuleAccess, {
  useCanExportPeople,
  useCanImportPeople,
  useCanInvitePeople,
} from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  useBulkActionMutation,
  useUpdateInviteStatusMutation,
  useDeleteCompanyUsersMutation,
  useDeleteGuestMutation,
  useDeleteGuestsMutation,
  useGetGuestsQuery,
  useGetPeopleQuery,
  useStartGuestExportMutation,
  useStartPeopleExportMutation,
  CompanyUserBulkActionType,
  CompanyUserStatus,
  type BulkActionMethod,
  type InviteStatus,
  type PersonListItem,
} from '@/api/endpoints/peopleApi';
import {
  useCreatePeopleTagMutation,
  useDeletePeopleTagMutation,
  useGetPeopleTagsQuery,
  useUpdatePeopleTagMutation,
} from '@/api/endpoints/tagsApi';
import { useGetGroupsSelectionQuery } from '@/api/endpoints/groupsApi';
import { useGetRolesSelectionQuery, type RoleOption } from '@/api/endpoints/rolesApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import type { OrgUnit } from '@/mocks/structure';
import TagsModal, { type TagDraft } from '@/pages/structure-page/TagsModal';
import { usePeopleColumns, PROFILE_COLUMN_KEYS } from './shared/peopleColumns';
import AddPeopleDrawer from './add-people/AddPeopleDrawer';
import AddGuestDrawer from './guests/AddGuestDrawer';
import GuestDetailDrawer from './guests/GuestDetailDrawer';
import PersonDetailDrawer from './members/PersonDetailDrawer';
import GuestRowActions from './guests/GuestRowActions';
import PersonRowActions from './members/PersonRowActions';
import GroupsTab from './groups/GroupsTab';
import PeopleBulkActions from './members/PeopleBulkActions';
import RoleBulkModal from './members/RoleBulkModal';
import StructureBulkModal from './members/StructureBulkModal';
import { EMPLOYMENT_TYPES, NATIONALITIES } from './shared/personFields';

// Flatten the structure tree into a flat unit list (same idiom as StructurePage).
const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

/** Just the row facts bulk-action conditions/copy need — not the whole row. */
interface SelectedPerson {
  id: string;
  isOwner: boolean;
  fullName: string;
  roles: { id: string; name: string }[];
  status: CompanyUserStatus;
}
const toSelected = (p: PersonListItem): SelectedPerson => ({
  id: p.id,
  isOwner: p.isOwner,
  fullName: p.fullName,
  roles: p.roles,
  status: p.status,
});

/** Roles held by at least one target, deduped by id — Remove role only offers
 *  what is actually removable, never the whole company role list. */
const roleUnion = (targets: SelectedPerson[]): RoleOption[] => {
  const byId = new Map<string, string>();
  targets.forEach((p) => p.roles.forEach((r) => byId.set(r.id, r.name)));
  return [...byId]
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title));
};

/** A bulk-action modal/confirm in flight — from the selection bar (all selected
 *  rows, selection clears on success) or a single row's kebab (one target,
 *  selection untouched). */
interface BulkTarget {
  type: CompanyUserBulkActionType;
  targets: SelectedPerson[];
  fromSelection: boolean;
}

/** A person delete in flight. `kind` picks the endpoint and the copy — Pending
 *  withdraws an admin invite, Archived removes the person, and the two have separate
 *  contracts. From the selection bar (every selected row, selection clears on
 *  success) or one row's kebab (one target, the rest of the selection
 *  untouched). Kept separate from BulkTarget: delete is not a
 *  CompanyUserBulkActionType, and from the guest delete state, whose modals
 *  render without a tab guard. */
interface PersonDeleteTarget {
  kind: 'invite' | 'archived';
  targets: SelectedPerson[];
  fromSelection: boolean;
}

/** An Accept/Reject on `pending` requests in flight — same row-vs-selection
 *  semantics as PersonDeleteTarget. */
interface InviteDecisionTarget {
  status: Extract<InviteStatus, 'ACCEPTED' | 'REJECTED'>;
  targets: SelectedPerson[];
  fromSelection: boolean;
}

// Filter section keys, and the empty selection shape used for init + reset.
const FILTER_KEYS = ['roles', 'units', 'groups', 'tags', 'employmentType', 'nationality'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

const PeoplePage = () => {
  const { t } = useTranslation();

  // Verb gates (hidden, not disabled, when missing). Invite/Import/Export are
  // the module's own extras — they do NOT ride create/view. Archive rides
  // `edit` by decision (the module has no archive slug).
  const { canCreate, canEdit, canDelete } = useModuleAccess('people');
  const canInvite = useCanInvitePeople();
  const canImport = useCanImportPeople();
  const canExport = useCanExportPeople();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // Selection stores per-row condition facts (id → SelectedPerson), not bare ids,
  // so bulk-action conditions (e.g. isOwner) keep working for rows selected on
  // earlier pages.
  const [selected, setSelected] = useState<Map<string, SelectedPerson>>(new Map());
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Which tab the Add-people drawer lands on (Add person vs Invite by link).
  const [addTab, setAddTab] = useState<'add-person' | 'invite' | 'import'>('add-person');
  // Add guest (add-only drawer) vs the Profile drawer (row click / kebab Edit —
  // view + inline edit). The Profile drawer is the single guest edit surface.
  const [addGuestOpen, setAddGuestOpen] = useState(false);
  // Detail drawer target — `edit` opens it straight into edit mode (kebab Edit).
  const [detailTarget, setDetailTarget] = useState<{ id: string; edit: boolean } | null>(null);
  // Person Profile drawer — opened in view mode by row click / kebab View.
  const [personDetailId, setPersonDetailId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PersonListItem | null>(null);
  const [deleteGuest, { isLoading: deletingGuest }] = useDeleteGuestMutation();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteGuests, { isLoading: bulkDeleting }] = useDeleteGuestsMutation();
  // People-tab bulk actions — which action's modal/confirm is open and for
  // which targets (null = none). Fed by the selection bar or a row's kebab.
  const [bulk, setBulk] = useState<BulkTarget | null>(null);
  const [personDelete, setPersonDelete] = useState<PersonDeleteTarget | null>(null);
  const [inviteDecision, setInviteDecision] = useState<InviteDecisionTarget | null>(null);
  const [updateInviteStatus, { isLoading: updatingInvites }] = useUpdateInviteStatusMutation();
  const [deleteCompanyUsers, { isLoading: deletingPeople }] = useDeleteCompanyUsersMutation();
  const [runBulkAction, { isLoading: bulkRunning }] = useBulkActionMutation();
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  const [activeTab, setActiveTab] = useState<
    'people' | 'pending' | 'guest' | 'archived' | 'groups'
  >('people');

  // Lightweight groups list (GET /groups/selection) for the toolbar Filter's
  // Groups options. The Groups badge count comes from the Groups tab itself
  // (reported via onCount), not from here.
  const { data: groupOptions = [] } = useGetGroupsSelectionQuery();

  const { data: peopleTags = [] } = useGetPeopleTagsQuery();
  const [startGuestExport] = useStartGuestExportMutation();
  const [startPeopleExport] = useStartPeopleExportMutation();
  const [createPeopleTag] = useCreatePeopleTagMutation();
  const [updatePeopleTag] = useUpdatePeopleTagMutation();
  const [deletePeopleTag] = useDeletePeopleTagMutation();

  // Filter option data — roles + structure units join the already-fetched
  // groups/tags to build the toolbar Filter's grouped sections.
  const { data: roles = [] } = useGetRolesSelectionQuery();
  const { data: structure = [] } = useGetStructureSelectionQuery();
  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'roles',
        label: t('people_col_roles'),
        icon: 'shield-check',
        searchable: true,
        searchPlaceholder: t('people_filter_roles_ph'),
        options: roles.map((r) => ({ id: r.id, label: r.title })),
      },
      {
        key: 'units',
        label: t('people_col_structure'),
        icon: 'network',
        searchable: true,
        searchPlaceholder: t('people_bulk_node_search_ph'),
        options: flattenUnits(structure)
          .filter((u) => u.level !== 0)
          .map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'groups',
        label: t('people_col_groups'),
        icon: 'users',
        searchable: true,
        searchPlaceholder: t('groups_search_ph'),
        options: groupOptions.map((g) => ({ id: g.id, label: g.name })),
      },
      {
        key: 'tags',
        label: t('people_col_tags'),
        icon: 'tag',
        searchable: true,
        searchPlaceholder: t('tags_picker_search'),
        options: peopleTags.map((tag) => ({ id: tag.id, label: tag.title })),
      },
      {
        key: 'employmentType',
        label: t('table_header_employment_type'),
        icon: 'briefcase',
        // Literal values — the personFields lists are backend-stored display
        // strings, not i18n copy.
        options: EMPLOYMENT_TYPES.map((v) => ({ id: v, label: v })),
      },
      {
        key: 'nationality',
        label: t('people_col_nationality'),
        icon: 'globe',
        searchable: true,
        searchPlaceholder: t('people_filter_nationality_ph'),
        options: NATIONALITIES.map((v) => ({ id: v, label: v })),
      },
    ],
    [t, roles, groupOptions, peopleTags, structure],
  );

  const anyFilter = Object.values(filters).some((s) => s.size > 0);
  const ids = (s: Set<string>) => (s.size ? [...s] : undefined);

  // One request at a time — always for the visible people-lifecycle tab.
  // Groups keeps serving 'active' behind the scenes (it ignores this list);
  // Guest swaps to its own endpoint, so the people query skips there.
  const listStatus: CompanyUserStatus[] =
    activeTab === 'pending'
      ? [CompanyUserStatus.PENDING, CompanyUserStatus.ADMIN_INVITE]
      : activeTab === 'archived'
        ? [CompanyUserStatus.ARCHIVED]
        : [CompanyUserStatus.ACTIVE];

  const peopleResult = useGetPeopleQuery(
    {
      page,
      limit,
      search: debouncedSearch || undefined,
      roles: ids(filters.roles),
      groups: ids(filters.groups),
      tags: ids(filters.tags),
      structureNodes: ids(filters.units),
      employmentType: ids(filters.employmentType),
      nationality: ids(filters.nationality),
      userStatus: listStatus,
    },
    { refetchOnMountOrArgChange: true, skip: activeTab === 'guest' },
  );
  const guestsResult = useGetGuestsQuery(
    { page, limit, search: debouncedSearch || undefined },
    { refetchOnMountOrArgChange: true, skip: activeTab !== 'guest' },
  );
  const { data, currentData, isLoading, isError } =
    activeTab === 'guest' ? guestsResult : peopleResult;
  // A new search term or filter pick reshuffles the result set — jump back to
  // the first page so we're never stranded on an out-of-range page.
  useEffect(() => setPage(1), [debouncedSearch, filters]);

  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);

  // Display-only memory of each tab's last-seen total (badges; no extra calls).
  // Only record while unfiltered so the badge stays the true total, not the
  // count of the current search/filter.
  const [lastTotals, setLastTotals] = useState<{
    people?: number;
    pending?: number;
    guest?: number;
    archived?: number;
    groups?: number;
  }>({});
  useEffect(() => {
    if (data && activeTab !== 'groups' && !debouncedSearch && !anyFilter)
      setLastTotals((c) => ({ ...c, [activeTab]: total }));
  }, [data, total, activeTab, debouncedSearch, anyFilter]);
  // The Groups tab reports its own total (GroupsTab.onCount); store it for the
  // badge. Stable + no-op when unchanged, so it won't loop.
  const recordGroupsCount = useCallback(
    (n: number) => setLastTotals((c) => (c.groups === n ? c : { ...c, groups: n })),
    [],
  );

  const columns = usePeopleColumns();

  // A row kebab action — one target, the page selection stays untouched.
  const openRowBulk = useCallback(
    (type: CompanyUserBulkActionType, person: PersonListItem) =>
      setBulk({ type, targets: [toSelected(person)], fromSelection: false }),
    [],
  );
  // A selection-bar action — every selected row (across pages) is a target.
  const openSelectionBulk = (type: CompanyUserBulkActionType) =>
    setBulk({ type, targets: [...selected.values()], fromSelection: true });
  // Delete from a row kebab — same shape either tab, the kind picks the call.
  const openRowDelete = useCallback(
    (kind: PersonDeleteTarget['kind'], person: PersonListItem) =>
      setPersonDelete({ kind, targets: [toSelected(person)], fromSelection: false }),
    [],
  );
  // Accept/Reject from a row kebab — one `pending` request.
  const openRowDecision = useCallback(
    (status: InviteDecisionTarget['status'], person: PersonListItem) =>
      setInviteDecision({ status, targets: [toSelected(person)], fromSelection: false }),
    [],
  );

  // Guests carry only identity data — trim to Person + Phone and swap the
  // shared inert Actions column for a functional kebab (Edit guest / Delete).
  const guestColumns: DataTableColumn<PersonListItem>[] = useMemo(
    () => [
      ...columns.filter((c) => c.key === 'person' || c.key === 'phone'),
      {
        key: 'actions',
        label: t('people_col_actions'),
        align: 'end',
        render: (guest) => (
          <GuestRowActions
            onEdit={canEdit ? () => setDetailTarget({ id: guest.id, edit: true }) : undefined}
            onDelete={canDelete ? () => setDeleteTarget(guest) : undefined}
          />
        ),
      },
    ],
    [columns, t, canEdit, canDelete],
  );
  // Column show/hide (guest tab). Actions stays locked so row actions are reachable.
  const { visibleColumns: visibleGuestColumns, menuProps: guestColumnsMenuProps } =
    useColumnVisibility(guestColumns, { excluded: ['actions'] });

  // People tab — swap the shared inert Actions cell for the row kebab
  // (View profile — same as row click — and Archive).
  const peopleTabColumns: DataTableColumn<PersonListItem>[] = useMemo(
    () =>
      columns.map((c) =>
        c.key === 'actions'
          ? {
              ...c,
              render: (person: PersonListItem) => (
                <PersonRowActions
                  variant='people'
                  onView={() => setPersonDetailId(person.id)}
                  onArchive={
                    canEdit
                      ? () => openRowBulk(CompanyUserBulkActionType.ARCHIVE, person)
                      : undefined
                  }
                />
              ),
            }
          : c,
      ),
    [columns, openRowBulk, canEdit],
  );

  // Pending/Archived — same columns, but the Actions cell is the tab's kebab
  // (Pending, by row status: a `pending` request gets Accept + Reject, riding
  // the invite extra; an `admin_invite` gets Delete, which withdraws it.
  // Archived: View profile + Restore + Delete, where Delete removes the person
  // for real and Restore moves them back to the People tab. Restore rides
  // `edit`, like Archive).
  const lifecycleTabColumns = useMemo(() => {
    const withKebab = (variant: 'pending' | 'archived') =>
      columns.map((c) =>
        c.key === 'actions'
          ? {
              ...c,
              render: (person: PersonListItem) => {
                const decidable =
                  variant === 'pending' && person.status === CompanyUserStatus.PENDING && canInvite;
                const deletable =
                  canDelete &&
                  (variant === 'archived' || person.status === CompanyUserStatus.ADMIN_INVITE);
                return (
                  <PersonRowActions
                    variant={variant}
                    onView={variant === 'archived' ? () => setPersonDetailId(person.id) : undefined}
                    onAccept={decidable ? () => openRowDecision('ACCEPTED', person) : undefined}
                    onReject={decidable ? () => openRowDecision('REJECTED', person) : undefined}
                    onDelete={
                      deletable
                        ? () => openRowDelete(variant === 'pending' ? 'invite' : 'archived', person)
                        : undefined
                    }
                    onRestore={
                      variant === 'archived' && canEdit
                        ? () => openRowBulk(CompanyUserBulkActionType.RESTORE, person)
                        : undefined
                    }
                  />
                );
              },
            }
          : c,
      );
    return { pending: withKebab('pending'), archived: withKebab('archived') };
  }, [columns, canEdit, canDelete, canInvite, openRowDelete, openRowDecision, openRowBulk]);

  // Column show/hide for the People / Pending / Archived tabs (Actions locked;
  // the optional profile columns start hidden — opt-in via the Columns menu).
  const { visibleColumns: visiblePeopleColumns, menuProps: peopleColumnsMenuProps } =
    useColumnVisibility(peopleTabColumns, {
      locked: ['person'],
      excluded: ['actions'],
      defaultHidden: PROFILE_COLUMN_KEYS,
    });
  const { visibleColumns: visiblePendingColumns, menuProps: pendingColumnsMenuProps } =
    useColumnVisibility(lifecycleTabColumns.pending, {
      locked: ['person'],
      excluded: ['actions'],
      defaultHidden: PROFILE_COLUMN_KEYS,
    });
  const { visibleColumns: visibleArchivedColumns, menuProps: archivedColumnsMenuProps } =
    useColumnVisibility(lifecycleTabColumns.archived, {
      locked: ['person'],
      excluded: ['actions'],
      defaultHidden: PROFILE_COLUMN_KEYS,
    });

  const items = data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  // Deleting the last rows on a page leaves it empty (the reset above only
  // fires for search/filter) — step back until a page has rows. Gated on
  // currentData so a stale previous page can't trigger an extra step.
  useEffect(() => {
    if (page > 1 && currentData && currentData.items.length === 0) setPage((p) => p - 1);
  }, [page, currentData]);
  // The shared DataTable takes a Set of ids — derived from the selection map.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  // Bulk role actions can't apply to an owner — the stored row objects make this
  // check correct even when the selection spans pages.
  const selectedHasOwner = useMemo(() => [...selected.values()].some((p) => p.isOwner), [selected]);
  // Pending-tab actions are status-restricted — a mixed selection disables the
  // ones that don't fit every selected row (stored statuses, so cross-page safe).
  const selectedAllPending = useMemo(
    () => [...selected.values()].every((p) => p.status === CompanyUserStatus.PENDING),
    [selected],
  );
  const selectedAllInvites = useMemo(
    () => [...selected.values()].every((p) => p.status === CompanyUserStatus.ADMIN_INVITE),
    [selected],
  );

  // Header select-all — over the current page's rows.
  const allSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const someSelected = items.some((p) => selected.has(p.id)) && !allSelected;
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (items.every((p) => next.has(p.id))) items.forEach((p) => next.delete(p.id));
      else items.forEach((p) => next.set(p.id, toSelected(p)));
      return next;
    });

  const switchTab = (value: string) => {
    setActiveTab(value as 'people' | 'pending' | 'guest' | 'archived' | 'groups');
    setPage(1);
    setSearch('');
    setFilters(emptyFilters());
    setSelected(new Map());
  };

  // DataTable hands back the row id; capture the row object from the loaded page
  // (the checkbox can only be clicked while its row is loaded).
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((p) => p.id === id);
        if (row) next.set(id, toSelected(row));
      }
      return next;
    });

  // Run the open bulk action on its targets, then close (a selection-bar run
  // also clears the selection — a row run leaves it untouched). `actionMethod`
  // is sent only for Assign role (add vs replace).
  const runBulk = async (actionIds: string[], actionMethod?: BulkActionMethod) => {
    if (!bulk) return;
    try {
      await runBulkAction({
        companyUserIds: bulk.targets.map((p) => p.id),
        actionType: bulk.type,
        actionIds,
        ...(actionMethod ? { actionMethod } : {}),
      }).unwrap();
      toast.success(t('people_bulk_done'));
      if (bulk.fromSelection) setSelected(new Map());
      setBulk(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Nothing reconciles `selected` against a refetch, so a run that moves rows
  // off the tab prunes them — a stale id would keep inflating the count and
  // holding the selection bar open. A selection-bar run clears it outright.
  const pruneSelection = (ids: string[], fromSelection: boolean) => {
    if (fromSelection) setSelected(new Map());
    else
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
  };

  // Delete what the open confirm targets — withdrawing admin invites (Pending)
  // or removing people (Archived).
  const runPersonDelete = async () => {
    if (!personDelete) return;
    const invites = personDelete.kind === 'invite';
    const ids = personDelete.targets.map((p) => p.id);
    try {
      if (invites) await updateInviteStatus({ companyUserIds: ids, status: 'DELETED' }).unwrap();
      else await deleteCompanyUsers({ companyUserIds: ids }).unwrap();
      toast.success(
        ids.length === 1
          ? t(invites ? 'people_pending_deleted' : 'people_archived_deleted')
          : t(invites ? 'people_pending_bulk_deleted' : 'people_archived_bulk_deleted', {
              count: ids.length,
            }),
      );
      pruneSelection(ids, personDelete.fromSelection);
      setPersonDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Accept or reject the open confirm's `pending` requests — either way the
  // rows leave the Pending tab.
  const runInviteDecision = async () => {
    if (!inviteDecision) return;
    const accept = inviteDecision.status === 'ACCEPTED';
    const ids = inviteDecision.targets.map((p) => p.id);
    try {
      await updateInviteStatus({ companyUserIds: ids, status: inviteDecision.status }).unwrap();
      toast.success(
        ids.length === 1
          ? t(accept ? 'people_pending_accepted' : 'people_pending_rejected')
          : t(accept ? 'people_pending_bulk_accepted' : 'people_pending_bulk_rejected', {
              count: ids.length,
            }),
      );
      pruneSelection(ids, inviteDecision.fromSelection);
      setInviteDecision(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Diff the modal draft against the fetched people tags and fire the matching
  // create / rename / delete mutations; the cache invalidation refetches.
  const saveTags = async (draft: TagDraft[]) => {
    const byId = new Map(peopleTags.map((tag) => [tag.id, tag.title]));
    const draftIds = new Set(draft.filter((d) => d.id).map((d) => d.id));
    const creates = draft.filter((d) => !d.id);
    const updates = draft.filter((d) => d.id && byId.get(d.id) !== d.title);
    const deletes = peopleTags.filter((tag) => !draftIds.has(tag.id));

    setSavingTags(true);
    try {
      await Promise.all([
        ...creates.map((d) => createPeopleTag({ title: d.title }).unwrap()),
        ...updates.map((d) => updatePeopleTag({ id: d.id as string, title: d.title }).unwrap()),
        ...deletes.map((tag) => deletePeopleTag(tag.id).unwrap()),
      ]);
      setTagsOpen(false);
      toast.success(t('structure_tags_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSavingTags(false);
    }
  };

  const startGuestExportFlow = () => {
    startGuestExport()
      .unwrap()
      .then(() => toast.success(t('guest_export_started')))
      .catch(() => toast.error(t('error_generic')));
  };

  // People export — same fire-and-forget shape; `status` picks the population
  // (People tab exports active, Archived tab exports archived).
  const startPeopleExportFlow = (status: CompanyUserStatus.ACTIVE | CompanyUserStatus.ARCHIVED) => {
    startPeopleExport({ status })
      .unwrap()
      .then(() => toast.success(t('people_export_started')))
      .catch(() => toast.error(t('error_generic')));
  };

  return (
    <div className='people-page'>
      <header className='people-page__header'>
        <div>
          <h1 className='people-page__title'>{t('people_title')}</h1>
          <p className='people-page__subtitle'>{t('people_subtitle')}</p>
        </div>
      </header>

      <Tabs
        aria-label={t('people_title')}
        value={activeTab}
        onChange={switchTab}
        items={[
          {
            value: 'people',
            label: t('people_tab_people'),
            icon: 'users',
            count: lastTotals.people,
          },
          {
            value: 'groups',
            label: t('people_tab_groups'),
            icon: 'users-round',
            count: lastTotals.groups,
          },
          {
            value: 'pending',
            label: t('people_tab_pending'),
            icon: 'clock',
            count: lastTotals.pending,
          },
          {
            value: 'guest',
            label: t('people_tab_guest'),
            icon: 'contact-round',
            count: lastTotals.guest,
          },
          {
            value: 'archived',
            label: t('people_tab_archived'),
            icon: 'archive',
            count: lastTotals.archived,
          },
        ]}
      />

      {activeTab === 'groups' && <GroupsTab onCount={recordGroupsCount} />}

      {activeTab !== 'groups' && (
        <div className='people-page__toolbar'>
          {/* Search + filter show on every people-lifecycle tab; the People
              action cluster (Tags/Export/Invite/Import/Add) is People-only, and
              the Guest tab gets its own Export + Add guest cluster. */}
          <div className='people-page__toolbar-lead'>
            <div className='people-page__search'>
              <Input
                size='sm'
                iconStart='search'
                placeholder={t('people_search_ph')}
                aria-label={t('field_search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <IconButton
                  className='people-page__search-clear'
                  icon='x'
                  variant='ghost'
                  size='sm'
                  aria-label={t('people_search_clear')}
                  onClick={() => setSearch('')}
                />
              )}
            </div>
            {/* Guests take no filter arrays — the Filter button hides there. */}
            {activeTab !== 'guest' && (
              <FilterDropdown
                label={t('action_filter')}
                categories={filterCategories}
                value={filters}
                onApply={setFilters}
                emptyText={t('text_no_results_found')}
              />
            )}
          </div>
          {activeTab === 'people' && (
            <div className='people-page__toolbar-actions'>
              {canEdit && (
                <Button variant='secondary' iconStart='tag' onClick={() => setTagsOpen(true)}>
                  {t('people_action_tags')}
                </Button>
              )}
              {canExport && (
                <Button
                  variant='secondary'
                  iconStart='download'
                  onClick={() => startPeopleExportFlow(CompanyUserStatus.ACTIVE)}
                >
                  {t('people_action_export')}
                </Button>
              )}
              {canInvite && (
                <Button
                  variant='secondary'
                  iconStart='link'
                  onClick={() => {
                    setAddTab('invite');
                    setAddOpen(true);
                  }}
                >
                  {t('people_action_invite')}
                </Button>
              )}
              {canImport && (
                <Button
                  variant='secondary'
                  iconStart='upload'
                  onClick={() => {
                    setAddTab('import');
                    setAddOpen(true);
                  }}
                >
                  {t('people_action_import')}
                </Button>
              )}
              {canCreate && (
                <Button
                  iconStart='user-plus'
                  onClick={() => {
                    setAddTab('add-person');
                    setAddOpen(true);
                  }}
                >
                  {t('people_action_add')}
                </Button>
              )}
            </div>
          )}
          {activeTab === 'guest' && (
            <div className='people-page__toolbar-actions'>
              {canExport && (
                <Button
                  variant='secondary'
                  iconStart='download'
                  onClick={() => startGuestExportFlow()}
                >
                  {t('people_action_export')}
                </Button>
              )}
              {canCreate && (
                <Button iconStart='user-plus' onClick={() => setAddGuestOpen(true)}>
                  {t('people_action_add_guest')}
                </Button>
              )}
            </div>
          )}
          {activeTab === 'archived' && canExport && (
            <div className='people-page__toolbar-actions'>
              <Button
                variant='secondary'
                iconStart='download'
                onClick={() => startPeopleExportFlow(CompanyUserStatus.ARCHIVED)}
              >
                {t('people_action_export')}
              </Button>
            </div>
          )}
        </div>
      )}

      {activeTab !== 'groups' && (
        <Card padded={false} className='people-page__panel'>
          {activeTab === 'guest' && (
            <>
              <TableToolbar count={total} countLabel={t('people_guests_word')}>
                <ColumnsMenu {...guestColumnsMenuProps} />
              </TableToolbar>
              {selected.size > 0 && (
                <div className='people-page__selection-bar'>
                  <div className='people-page__selection-lead'>
                    <span className='people-page__selected'>
                      {t('people_selected_count', { count: selected.size })}
                    </span>
                    {canDelete && (
                      <>
                        <span className='people-page__selection-divider' />
                        <Button
                          variant='ghost'
                          iconStart='trash-2'
                          onClick={() => setBulkDeleteOpen(true)}
                        >
                          {t('button_delete')}
                        </Button>
                      </>
                    )}
                  </div>
                  <Button variant='ghost' onClick={() => setSelected(new Map())}>
                    {t('people_selection_clear')}
                  </Button>
                </div>
              )}
            </>
          )}
          {activeTab === 'people' && (
            <TableToolbar count={total} countLabel={t('people_people_word')}>
              <ColumnsMenu {...peopleColumnsMenuProps} />
            </TableToolbar>
          )}
          {activeTab === 'pending' && (
            <TableToolbar count={total} countLabel={t('people_pending_word')}>
              <ColumnsMenu {...pendingColumnsMenuProps} />
            </TableToolbar>
          )}
          {activeTab === 'archived' && (
            <TableToolbar count={total} countLabel={t('people_archived_word')}>
              <ColumnsMenu {...archivedColumnsMenuProps} />
            </TableToolbar>
          )}
          {/* The selection bar's gated items, in order: the Bulk actions menu
              (People + Pending) for the role/structure edits, then the tab's
              lifecycle move — Archive on People, Accept + Reject on Pending,
              Restore on Archived — then Delete on Pending and Archived,
              withdrawing admin invites and removing people respectively. The
              Pending moves are status-restricted: disabled (with a tooltip)
              unless every selected row is `pending` (Accept/Reject) or
              `admin_invite` (Delete). Each gated item carries its own leading
              divider, so every permission combination renders exactly one per
              item. */}
          {(activeTab === 'people' || activeTab === 'pending' || activeTab === 'archived') &&
            selected.size > 0 && (
              <div className='people-page__selection-bar'>
                <div className='people-page__selection-lead'>
                  <span className='people-page__selected'>
                    {t('people_selected_count', { count: selected.size })}
                  </span>
                  {(activeTab === 'people' || activeTab === 'pending') && canEdit && (
                    <>
                      <span className='people-page__selection-divider' />
                      <PeopleBulkActions
                        disableRoleActions={selectedHasOwner}
                        onAssignRole={() =>
                          openSelectionBulk(CompanyUserBulkActionType.ASSIGN_ROLE)
                        }
                        onRemoveRole={() =>
                          openSelectionBulk(CompanyUserBulkActionType.REMOVE_ROLE)
                        }
                        onAddToStructure={() =>
                          openSelectionBulk(CompanyUserBulkActionType.ADD_TO_STRUCTURE)
                        }
                        onRemoveFromStructure={() =>
                          openSelectionBulk(CompanyUserBulkActionType.REMOVE_FROM_STRUCTURE)
                        }
                      />
                    </>
                  )}
                  {activeTab === 'people' && canEdit && (
                    <>
                      <span className='people-page__selection-divider' />
                      <Button
                        variant='ghost'
                        iconStart='archive'
                        onClick={() => openSelectionBulk(CompanyUserBulkActionType.ARCHIVE)}
                      >
                        {t('people_bulk_archive')}
                      </Button>
                    </>
                  )}
                  {activeTab === 'pending' && canInvite && (
                    <>
                      <span className='people-page__selection-divider' />
                      {(['ACCEPTED', 'REJECTED'] as const).map((status) => (
                        <Button
                          key={status}
                          variant='ghost'
                          iconStart={status === 'ACCEPTED' ? 'user-check' : 'user-x'}
                          disabled={!selectedAllPending}
                          aria-disabled={!selectedAllPending}
                          title={
                            selectedAllPending ? undefined : t('people_pending_decision_disabled')
                          }
                          onClick={() =>
                            setInviteDecision({
                              status,
                              targets: [...selected.values()],
                              fromSelection: true,
                            })
                          }
                        >
                          {t(
                            status === 'ACCEPTED'
                              ? 'people_pending_accept'
                              : 'people_pending_reject',
                          )}
                        </Button>
                      ))}
                    </>
                  )}
                  {activeTab === 'archived' && canEdit && (
                    <>
                      <span className='people-page__selection-divider' />
                      <Button
                        variant='ghost'
                        iconStart='archive-restore'
                        onClick={() => openSelectionBulk(CompanyUserBulkActionType.RESTORE)}
                      >
                        {t('people_menu_restore')}
                      </Button>
                    </>
                  )}
                  {(activeTab === 'pending' || activeTab === 'archived') && canDelete && (
                    <>
                      <span className='people-page__selection-divider' />
                      <Button
                        variant='ghost'
                        iconStart='trash-2'
                        disabled={activeTab === 'pending' && !selectedAllInvites}
                        aria-disabled={activeTab === 'pending' && !selectedAllInvites}
                        title={
                          activeTab === 'pending' && !selectedAllInvites
                            ? t('people_pending_delete_disabled')
                            : undefined
                        }
                        onClick={() =>
                          setPersonDelete({
                            kind: activeTab === 'pending' ? 'invite' : 'archived',
                            targets: [...selected.values()],
                            fromSelection: true,
                          })
                        }
                      >
                        {t('button_delete')}
                      </Button>
                    </>
                  )}
                </div>
                <Button variant='ghost' onClick={() => setSelected(new Map())}>
                  {t('people_selection_clear')}
                </Button>
              </div>
            )}
          <DataTable
            columns={
              activeTab === 'guest'
                ? visibleGuestColumns
                : activeTab === 'people'
                  ? visiblePeopleColumns
                  : activeTab === 'pending'
                    ? visiblePendingColumns
                    : activeTab === 'archived'
                      ? visibleArchivedColumns
                      : columns
            }
            rows={items}
            rowKey={(p) => p.id}
            selectable
            selectedIds={selectedIdSet}
            onToggleSelect={toggleSelect}
            // Select-all applies to every lifecycle tab (the table only renders
            // for non-group tabs).
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={toggleSelectAll}
            selectAllLabel={t('people_select_all')}
            {...(activeTab === 'guest'
              ? { onRowClick: (g: PersonListItem) => setDetailTarget({ id: g.id, edit: false }) }
              : activeTab === 'people'
                ? { onRowClick: (p: PersonListItem) => setPersonDetailId(p.id) }
                : {})}
            loading={isLoading}
            error={isError}
            loadingText={t('text_loading')}
            errorText={t('error_generic')}
            // Two guest columns don't need the wide-table h-scroll floor.
            minWidth={activeTab === 'guest' ? undefined : 1280}
            empty={
              <>
                <Icon
                  name={
                    activeTab === 'pending'
                      ? 'clock'
                      : activeTab === 'guest'
                        ? 'contact-round'
                        : activeTab === 'archived'
                          ? 'archive'
                          : 'users'
                  }
                  size={26}
                />
                {t('people_empty')}
              </>
            }
          />

          <TableFooter
            page={page}
            limit={limit}
            total={total}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n);
              setPage(1);
            }}
            perPageLabel={t('people_items_per_page')}
            rangeText={t(activeTab === 'guest' ? 'people_range_guests' : 'people_range', {
              from,
              to,
              total,
            })}
          />
        </Card>
      )}

      <TagsModal
        open={tagsOpen}
        tags={peopleTags}
        saving={savingTags}
        onClose={() => setTagsOpen(false)}
        onSave={saveTags}
        title={t('people_tags_title')}
        note={t('people_tags_note')}
        emptyText={t('people_tags_empty')}
      />
      {/* Drawers mount only while open — no wasted state/effects/queries when closed. */}
      {addOpen && <AddPeopleDrawer open onClose={() => setAddOpen(false)} initialTab={addTab} />}
      {addGuestOpen && <AddGuestDrawer open onClose={() => setAddGuestOpen(false)} />}
      {detailTarget && (
        <GuestDetailDrawer
          open
          guestId={detailTarget.id}
          startInEdit={detailTarget.edit}
          onClose={() => setDetailTarget(null)}
        />
      )}
      {personDetailId && (
        <PersonDetailDrawer
          open
          personId={personDetailId}
          onClose={() => setPersonDetailId(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          open
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            try {
              await deleteGuest(deleteTarget.id).unwrap();
              toast.success(t('guest_deleted'));
              setDeleteTarget(null);
            } catch {
              toast.error(t('error_generic'));
            }
          }}
          tone='danger'
          icon='trash-2'
          title={t('guest_delete_confirm_title', { name: deleteTarget.fullName })}
          description={t('guest_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deletingGuest}
        />
      )}
      {bulkDeleteOpen && (
        <ConfirmModal
          open
          onClose={() => setBulkDeleteOpen(false)}
          onConfirm={async () => {
            try {
              await deleteGuests({ guestIds: [...selected.keys()] }).unwrap();
              toast.success(t('guests_deleted'));
              setSelected(new Map());
              setBulkDeleteOpen(false);
            } catch {
              toast.error(t('error_generic'));
            }
          }}
          tone='danger'
          icon='trash-2'
          title={t('guest_bulk_delete_title', { count: selected.size })}
          description={t('guest_bulk_delete_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={bulkDeleting}
        />
      )}
      {/* One confirm for every person-delete path — Pending or Archived, from a
          row kebab (singular copy) or the selection bar (count copy). */}
      {personDelete && (
        <ConfirmModal
          open
          onClose={() => setPersonDelete(null)}
          onConfirm={runPersonDelete}
          tone='danger'
          icon='trash-2'
          title={
            personDelete.targets.length === 1
              ? t(
                  personDelete.kind === 'invite'
                    ? 'people_pending_delete_title'
                    : 'people_archived_delete_title',
                )
              : t(
                  personDelete.kind === 'invite'
                    ? 'people_pending_bulk_delete_title'
                    : 'people_archived_bulk_delete_title',
                  { count: personDelete.targets.length },
                )
          }
          description={
            personDelete.targets.length === 1
              ? t(
                  personDelete.kind === 'invite'
                    ? 'people_pending_delete_body'
                    : 'people_archived_delete_body',
                )
              : t(
                  personDelete.kind === 'invite'
                    ? 'people_pending_bulk_delete_body'
                    : 'people_archived_bulk_delete_body',
                )
          }
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={updatingInvites || deletingPeople}
        />
      )}
      {/* Accept/Reject confirm for `pending` requests — row kebab (singular
          copy) or the selection bar (count copy). */}
      {inviteDecision && (
        <ConfirmModal
          open
          onClose={() => setInviteDecision(null)}
          onConfirm={runInviteDecision}
          tone={inviteDecision.status === 'ACCEPTED' ? 'success' : 'danger'}
          icon={inviteDecision.status === 'ACCEPTED' ? 'user-check' : 'user-x'}
          title={
            inviteDecision.targets.length === 1
              ? t(
                  inviteDecision.status === 'ACCEPTED'
                    ? 'people_pending_accept_title'
                    : 'people_pending_reject_title',
                )
              : t(
                  inviteDecision.status === 'ACCEPTED'
                    ? 'people_pending_bulk_accept_title'
                    : 'people_pending_bulk_reject_title',
                  { count: inviteDecision.targets.length },
                )
          }
          description={
            inviteDecision.targets.length === 1
              ? t(
                  inviteDecision.status === 'ACCEPTED'
                    ? 'people_pending_accept_body'
                    : 'people_pending_reject_body',
                )
              : t(
                  inviteDecision.status === 'ACCEPTED'
                    ? 'people_pending_bulk_accept_body'
                    : 'people_pending_bulk_reject_body',
                )
          }
          confirmLabel={t(
            inviteDecision.status === 'ACCEPTED'
              ? 'people_pending_accept'
              : 'people_pending_reject',
          )}
          confirmVariant={inviteDecision.status === 'ACCEPTED' ? 'primary' : 'danger'}
          loading={updatingInvites}
        />
      )}

      {(bulk?.type === CompanyUserBulkActionType.ASSIGN_ROLE ||
        bulk?.type === CompanyUserBulkActionType.REMOVE_ROLE) && (
        <RoleBulkModal
          open
          variant={bulk.type === CompanyUserBulkActionType.ASSIGN_ROLE ? 'assign' : 'remove'}
          peopleCount={bulk.targets.length}
          roles={
            bulk.type === CompanyUserBulkActionType.REMOVE_ROLE ? roleUnion(bulk.targets) : roles
          }
          loading={bulkRunning}
          onClose={() => setBulk(null)}
          onConfirm={(roleIds, method) => runBulk(roleIds, method)}
        />
      )}
      {(bulk?.type === CompanyUserBulkActionType.ADD_TO_STRUCTURE ||
        bulk?.type === CompanyUserBulkActionType.REMOVE_FROM_STRUCTURE) && (
        <StructureBulkModal
          open
          variant={bulk.type === CompanyUserBulkActionType.ADD_TO_STRUCTURE ? 'add' : 'remove'}
          peopleCount={bulk.targets.length}
          tree={structure}
          loading={bulkRunning}
          onClose={() => setBulk(null)}
          onConfirm={(nodeIds) => runBulk(nodeIds)}
        />
      )}
      {bulk?.type === CompanyUserBulkActionType.ARCHIVE && (
        <ConfirmModal
          open
          onClose={() => setBulk(null)}
          onConfirm={() => runBulk([])}
          tone='warning'
          icon='archive'
          // One target (a row kebab, or a single selection) gets the named
          // singular copy; more get the count copy.
          title={
            bulk.targets.length === 1
              ? t('people_archive_confirm_title', { name: bulk.targets[0].fullName })
              : t('people_bulk_archive_title', { count: bulk.targets.length })
          }
          description={
            bulk.targets.length === 1
              ? t('people_archive_confirm_body')
              : t('people_bulk_archive_body')
          }
          confirmLabel={t('people_bulk_archive')}
          loading={bulkRunning}
        />
      )}
      {bulk?.type === CompanyUserBulkActionType.RESTORE && (
        <ConfirmModal
          open
          onClose={() => setBulk(null)}
          onConfirm={() => runBulk([])}
          tone='brand'
          icon='archive-restore'
          // Same split as Archive: one target gets the named singular copy,
          // more get the count copy.
          title={
            bulk.targets.length === 1
              ? t('people_restore_confirm_title', { name: bulk.targets[0].fullName })
              : t('people_bulk_restore_title', { count: bulk.targets.length })
          }
          description={
            bulk.targets.length === 1
              ? t('people_restore_confirm_body')
              : t('people_bulk_restore_body')
          }
          confirmLabel={t('people_menu_restore')}
          loading={bulkRunning}
        />
      )}
    </div>
  );
};

export default PeoplePage;
